/**
 * Allowed-origin helpers for the editors.
 *
 * All three behaviours defer to `@hotline/contracts` so the dashboard, the API's
 * write validation and the CORS matcher cannot disagree about what an origin is.
 * They disagreed before, and the result was an entry the dashboard happily
 * accepted (`app.example.com`), the API happily stored, and the matcher could
 * never match — a widget that 403'd every submission with nothing to show for it.
 */
import { ORIGIN_WILDCARD, describeOriginProblem, normalizeOrigin } from '@hotline/contracts';

/** An error message if this entry could never match, or `null` if it is fine. */
export function validateAllowedOrigin(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === ORIGIN_WILDCARD) return null;
  return normalizeOrigin(trimmed) === null ? describeOriginProblem(trimmed) : null;
}

/**
 * The form the API will store, so the chip shows what is actually saved rather
 * than what was typed (`https://App.Example.com/` → `https://app.example.com`).
 */
export function canonicalizeAllowedOrigin(value: string): string {
  const trimmed = value.trim();
  if (trimmed === ORIGIN_WILDCARD) return ORIGIN_WILDCARD;
  return normalizeOrigin(trimmed) ?? trimmed;
}

/**
 * A warning for an ALREADY-STORED entry, or `null`. Existing rows are never
 * rejected on read (that would 500 the editor the operator needs in order to fix
 * them), so bad ones are flagged here instead.
 */
export function warnAboutStoredOrigin(value: string): string | null {
  const problem = validateAllowedOrigin(value);
  if (problem) return `${problem}. This entry never matches, so it does nothing.`;
  const canonical = canonicalizeAllowedOrigin(value);
  if (canonical !== value.trim()) {
    return `Will be saved as ${canonical}.`;
  }
  return null;
}
